import { backgroundTypeEnum } from "common/utils/enums";
import LogoImage from "common/components/logo-image";
import Button from "common/components/button";
import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { ButtonGroup } from "@chakra-ui/react";

const PropertyBadge = ({ property, outline = false, className, useAltLogo = false }) => {
  const brand = property?.brand;
  const logoToDisplay = useAltLogo ? brand?.logoAlt : brand?.logo;

  return (
    <div
      className={`grid grid-cols-[auto_1fr] items-center gap-x-6 gap-y-1 w-fit h-fit py-4 pl-6 pr-8 rounded-xl border ${
        outline
          ? brand?.backgroundType === backgroundTypeEnum.BACKGROUND_DARK
            ? "border-white"
            : "border-black"
          : "border-gray-200"
      } ${className}`}
      style={{
        backgroundColor: !outline && brand?.bgColor,
      }}
    >
      <img
        src={logoToDisplay}
        alt={`${property?.name} Logo`}
        className="object-contain h-auto w-full max-w-[80px] max-h-[40px] sm:max-w-[100px] sm:max-h-[50px] row-span-2"
        style={{
          filter:
            outline && !useAltLogo
              ? brand?.backgroundType === backgroundTypeEnum.BACKGROUND_DARK
                ? "saturate(0) brightness(100)"
                : "saturate(0) brightness(0)"
              : "none",
        }}
      />      
      <div className="flex flex-col gap-2 sm:gap-0">
        <span
          className={`${
            brand?.backgroundType === backgroundTypeEnum.BACKGROUND_DARK
              ? "text-white"
              : "text-black"
          } text-xl sm:text-2xl font-display tracking-tight leading-tight text-left`}
        >
          Love It or Leave It Guarantee
        </span>
      </div>
      <div className="flex gap-1.5 items-baseline sm:self-end -mr-1 flex-row">
          <span
            className={`${
              brand?.backgroundType === backgroundTypeEnum.BACKGROUND_DARK
                ? "text-white opacity-70"
                : "text-gray-700"
            } text-xs font-medium`}
          >
            Powered by
          </span>
          <LogoImage
            dark={brand?.backgroundType === backgroundTypeEnum.BACKGROUND_LIGHT}
            size="3xs"
          />
        </div>
    </div>
  );
};

const DownloadPropertyBadge = ({ property }) => {
  const badgeRef = useRef(null);
  const [outline, setOutline] = useState(false);
  const [useAltLogo, setUseAltLogo] = useState(false);
  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex flex-col sm:flex-row gap-4 sm:gap-2 justify-between sm:items-center">
        <span className={`text-base font-medium`}>ReLease Badge</span>
        <ButtonGroup className="overflow-x-auto max-w-full sm:overflow-visible sm:max-w-none whitespace-nowrap">
          <Button
            title={useAltLogo ? "Use Standard Logo" : "Use Alt Logo"}
            wFull={false}
            theme="secondary"
            onClick={() => setUseAltLogo(!useAltLogo)}
            size="sm"
          />
          <Button
            title="Change Style"
            wFull={false}
            theme="secondary"
            onClick={() => setOutline(!outline)}
            size="sm"
          />
          <Button
            title="Download as PNG"
            wFull={false}
            theme="primary"
            onClick={() => {
              if (badgeRef.current) {
                toPng(badgeRef.current, {
                  backgroundColor: null,
                  cacheBust: true,
                  pixelRatio: 4,
                })
                  .then((dataUrl) => {
                    const link = document.createElement("a");
                    const baseName =
                      property?.name?.replace(/\s+/g, "_") || "badge";
                    const fileName = `${baseName}_badge${
                      outline ? "_outline" : ""
                    }.png`;
                    link.href = dataUrl;
                    link.download = fileName;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  })
                  .catch((error) => {
                    console.error("Export failed:", error);
                  });
              }
            }}
            size="sm"
          />
        </ButtonGroup>
      </div>
      <div className="flex justify-center p-4 pt-4 border border-gray-200 rounded-md relative">
        <div
          className={`rounded-xl ${
            outline &&
            property?.brand?.backgroundType ===
              backgroundTypeEnum.BACKGROUND_DARK
              ? "bg-gray-200"
              : "bg-white"
          }`}
        >
          <div
            style={{
              backgroundImage: `linear-gradient(45deg, rgba(170,170,170,0.1) 25%, transparent 25%, transparent 75%, rgba(170,170,170,0.1) 75%, rgba(170,170,170,0.1)), linear-gradient(45deg, rgba(170,170,170,0.1) 25%, transparent 25%, transparent 75%, rgba(170,170,170,0.1) 75%, rgba(170,170,170,0.1))`,
              backgroundSize: "16px 16px",
              backgroundPosition: "0 0, 8px 8px",
            }}
          >
            <div ref={badgeRef} className="inline-block">
              <PropertyBadge
                property={property}
                outline={outline}
                useAltLogo={useAltLogo}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { PropertyBadge, DownloadPropertyBadge };
